// ============================================================
// ANSICHTSSACHE-AGENT – Anmeldeformular
// ============================================================

"use client";

import { useActionState, useId, useState } from "react";
import { AlertCircle, ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { Feld, FELD } from "../feld";
import { loginAction } from "./actions";

// Keine Vorbelegung — auch nicht im Entwicklungsserver. Bis zum
// 22.10.2026 stand unter agent.dev das Admin-Kennwort im HTML, und
// agent.dev ist oeffentlich erreichbar und haengt an derselben
// Datenbank wie Live. Die Browserpruefungen (bin/design-check.mjs,
// bin/anleitung-bilder.mjs) tippen selbst; ihre Zugangsdaten kommen
// aus PRUEF_NUTZER/PRUEF_PASSWORT bzw. der Datei schluessel/pruef.env
// ausserhalb des Repos.
export function LoginForm() {
  const [zeigePasswort, setZeigePasswort] = useState(false);
  // Adresse und Kennwort gefuehrt statt frei: React leert ein
  // Formular nach jeder Server Action. Frei gaebe es nach einem
  // Tippfehler die Adresse und im Schritt „Code aus der App“ das
  // Kennwort nicht mehr her — der Code allein meldet niemanden an.
  const [email, setEmail] = useState("");
  const [passwort, setPasswort] = useState("");
  const id = useId();

  // `action` statt `onSubmit`: React rendert das Formular damit
  // als POST auf die Server Action. Wer abschickt, bevor der
  // Browser das JavaScript geladen hat, meldet sich trotzdem an
  // — vorher schickte der Browser in diesem Moment ein GET mit
  // Passwort in der Adresszeile. Warum das so wichtig ist,
  // steht in `actions.ts`.
  const [zustand, anmelden, isPending] = useActionState(loginAction, null);

  return (
    <form action={anmelden} className="space-y-5">
      {zustand?.error && (
        <div
          role="alert"
          className="border-danger/25 bg-danger/10 text-body-sm text-danger flex items-start gap-2.5 rounded-lg border px-3.5 py-3"
        >
          <AlertCircle className="mt-px h-4 w-4 shrink-0" aria-hidden />
          <span>{zustand.error}</span>
        </div>
      )}

      <Feld label="E-Mail" htmlFor={`${id}-email`}>
        <input
          id={`${id}-email`}
          name="email"
          type="email"
          required
          autoComplete="username"
          placeholder="name@agentur.de"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={isPending}
          className={FELD}
        />
      </Feld>

      <Feld label="Passwort" htmlFor={`${id}-passwort`}>
        <div className="relative">
          <input
            id={`${id}-passwort`}
            name="password"
            type={zeigePasswort ? "text" : "password"}
            required
            autoComplete="current-password"
            placeholder="••••••••"
            value={passwort}
            onChange={(event) => setPasswort(event.target.value)}
            disabled={isPending}
            className={`${FELD} pr-11`}
          />
          {/* Tippfehler im Passwort sind der haeufigste Grund fuer
              eine fehlgeschlagene Anmeldung. Ein Auge daneben ist
              billiger als ein zweiter Versuch. */}
          <button
            type="button"
            onClick={() => setZeigePasswort((v) => !v)}
            disabled={isPending}
            aria-label={zeigePasswort ? "Passwort verbergen" : "Passwort anzeigen"}
            className="text-text-tertiary duration-fast hover:text-text-primary absolute top-1 right-1 grid h-9 w-9 place-items-center rounded-md transition-colors"
          >
            {zeigePasswort ? (
              <EyeOff className="h-4 w-4" aria-hidden />
            ) : (
              <Eye className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>
      </Feld>

      {/* Der zweite Schritt: erst, wenn das Kennwort gestimmt hat
          und das Konto den zweiten Faktor traegt. Frei gefuehrt —
          nach einem falschen Code soll das Feld leer sein. */}
      {zustand?.zweiterFaktor && (
        <Feld label="Code aus der Authenticator-App" htmlFor={`${id}-code`}>
          <input
            id={`${id}-code`}
            name="totpCode"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            required
            autoFocus
            maxLength={7}
            placeholder="123456"
            disabled={isPending}
            className={FELD}
            aria-describedby={`${id}-code-hinweis`}
          />
          <p id={`${id}-code-hinweis`} className="text-caption text-text-tertiary">
            Für dieses Konto ist der zweite Faktor eingeschaltet: Gib den sechsstelligen Code ein,
            den deine App gerade zeigt.
          </p>
        </Feld>
      )}

      <button
        type="submit"
        disabled={isPending}
        className="group bg-accent text-body-md text-accent-contrast shadow-e2 duration-fast ease-standard hover:bg-accent-hover hover:shadow-e3 mt-2 flex h-11 w-full items-center justify-center gap-2 rounded-lg font-medium transition-all active:scale-[0.985] disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
      >
        {isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Wird angemeldet…
          </>
        ) : (
          <>
            Anmelden
            {/* Der Pfeil rueckt beim Zeigen nach — winzig, aber es
                macht den Knopf ansprechbar statt gedruckt. */}
            <ArrowRight
              className="duration-fast ease-standard h-4 w-4 transition-transform group-hover:translate-x-0.5"
              aria-hidden
            />
          </>
        )}
      </button>
    </form>
  );
}
